import { useEffect, useMemo, useState } from "react";
import { MdOutlineDeliveryDining as Moto } from "react-icons/md";
import ChangePassword from "../../../cors/ChangePassword";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { BASE_URL } from "../../../apiConfig";
import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";

export default function ProfileLivreur({ user }) {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [livreurData, setLivreurData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    rib: "",
    prix_delivery: "",
  });

  useEffect(() => {
    const attachedLivreur = user?.livreur_info || null;
    if (attachedLivreur) {
      setLivreurData(attachedLivreur);
      setFormData({
        name: attachedLivreur.name || "",
        email: user?.email || "",
        phone: attachedLivreur.phone || "",
        rib: attachedLivreur.rib || "",
        prix_delivery: attachedLivreur.prix_delivery || "",
      });
    }
  }, [user]);

  const headerName = useMemo(() => formData.name || "Livreur", [formData.name]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = [t("profile.livreur.name_error")];
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.email = [t("profile.livreur.email_error")];
    }

    const phoneRegex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/;
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      errors.phone = [t("profile.livreur.phone_error")];
    }

    if (formData.rib && formData.rib.length < 10) {
      errors.rib = [t("profile.livreur.rib_error")];
    }

    return errors;
  };

  const handleSubmit = async () => {
    if (!livreurData?.id) {
      toast.error(t("profile.livreur.info_not_found"));
      return;
    }

    const clientErrors = validateForm();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      toast.error(t("profile.livreur.validation_error"));
      return;
    }

    setIsLoading(true);
    setFieldErrors({});

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        rib: formData.rib,
      };

      const response = await fetch(`${BASE_URL}/api/livreurs/${livreurData.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.errors) {
          setFieldErrors(result.errors);
          toast.error(t("profile.livreur.server_error"));
          return;
        }
        throw new Error(result.message || t("profile.livreur.update_error"));
      }

      const updatedLivreur = result.data || { ...livreurData, ...payload };
      setLivreurData(updatedLivreur);
      const updatedUser = {
        ...user,
        email: formData.email,
        livreur_info: updatedLivreur,
      };
      Cookies.set("user", JSON.stringify(updatedUser), { expires: 7 });

      setIsEditing(false);
      toast.success(t("profile.livreur.update_success"));
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || t("profile.livreur.update_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: livreurData?.name || "",
      email: user?.email || "",
      phone: livreurData?.phone || "",
      rib: livreurData?.rib || "",
      prix_delivery: livreurData?.prix_delivery || "",
    });
    setFieldErrors({});
    setIsEditing(false);
  };

  if (!user) {
    return <div className="max-w-6xl mx-auto p-6">{t('profile.livreur.loading')}</div>;
  }

  if (!livreurData) {
    return <div className="max-w-6xl mx-auto p-6">{t('profile.livreur.not_found')}</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 md:w-32 md:h-32 bg-white/20 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                <Moto className="h-10 w-10 md:w-16 md:h-16 text-white" />
              </div>
              <div className="text-white">
                <h1 className="text-xl md:text-2xl font-bold">{headerName}</h1>
                <p className="text-xs text-emerald-100 mb-1">{formData.email}</p>
                <p className="text-emerald-100 text-xs">{t('profile.livreur.badge')}</p>
              </div>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2 text-center text-white">
                <div className="text-xl font-bold">{t('profile.livreur.badge')}</div>
                <div className="text-xs opacity-80">{t('profile.livreur.delivery_price_label')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        <div className="p-8 max-xl:px-5">
          <div className="space-y-6">
            <div>
              {!isEditing ? (
                <div className="flex justify-end">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)] cursor-pointer px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <span className="text-sm max-xl:text-xs">{t('profile.livreur.edit')}</span>
                  </button>
                </div>
              ) : (
                <div className="flex justify-end gap-2">
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className={`bg-[var(--color-primary-500)] text-white cursor-pointer px-6 py-2 rounded-lg font-semibold hover:bg-[var(--color-primary-400)] transition-colors duration-200 flex items-center gap-2 ${
                      isLoading ? "opacity-70" : ""
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="max-xl:text-xs">{t('profile.livreur.saving')}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm max-xl:text-xs">{t('profile.livreur.save')}</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="bg-red-100 text-red-600 cursor-pointer px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm max-xl:text-xs">{t('profile.livreur.cancel')}</span>
                  </button>
                </div>
              )}
            </div>

            <div className="flex max-xl:px-3">
              <div className="flex items-center mb-4 gap-3">
                <div className="bg-[var(--color-primary-50)] p-3 rounded-lg">
                  <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800">{t('profile.livreur.title')}</h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-xl:px-3">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.livreur.full_name')}</label>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.name
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                      {fieldErrors.name && <p className="text-red-500 text-xs mt-1">{fieldErrors.name[0]}</p>}
                    </>
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{formData.name || t('profile.livreur.not_specified')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.livreur.phone')}</label>
                  {isEditing ? (
                    <>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.phone
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                      {fieldErrors.phone && <p className="text-red-500 text-xs mt-1">{fieldErrors.phone[0]}</p>}
                    </>
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{formData.phone || t('profile.livreur.not_specified')}</p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.livreur.email')}</label>
                  {isEditing ? (
                    <>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.email
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                      {fieldErrors.email && <p className="text-red-500 text-xs mt-1">{fieldErrors.email[0]}</p>}
                    </>
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{formData.email || t('profile.livreur.not_specified')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.livreur.rib')}</label>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        name="rib"
                        value={formData.rib}
                        onChange={handleInputChange}
                        className={`w-full font-mono py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.rib
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                      {fieldErrors.rib && <p className="text-red-500 text-xs mt-1">{fieldErrors.rib[0]}</p>}
                    </>
                  ) : (
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl font-mono">{formData.rib || t('profile.livreur.not_specified')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.livreur.delivery_price')}</label>
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">{formData.prix_delivery || "0"} DH</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ChangePassword userId={user.id} />
    </div>
  );
}


